import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BackToTop from '../components/layout/BackToTop';
import blogPageContent from '../content/blog/index.json';
import { Newspaper, Calendar, Clock, Tag as TagIcon, ArrowRight, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
};

const BlogIndexPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTag, setActiveTag] = useState(null);

  useEffect(() => {
    document.title = 'Blog — ResiAX | Homony';
    window.scrollTo(0, 0);

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API}/api/blog`);
        if (!res.ok) throw new Error(`Failed to load posts (${res.status})`);
        const data = await res.json();
        setPosts(data.posts || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const allTags = useMemo(() => {
    const set = new Set();
    posts.forEach((p) => (p.tags || []).forEach((t) => set.add(t)));
    return Array.from(set);
  }, [posts]);

  const visiblePosts = useMemo(
    () => (activeTag ? posts.filter((p) => p.tags?.includes(activeTag)) : posts),
    [posts, activeTag]
  );

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#1F1E1E] to-[#2D2A2A] text-white py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 bg-[#B5541B]/20 px-4 py-2 rounded-full mb-6">
              <Newspaper className="w-5 h-5 text-[#D4722F]" />
              <span className="text-sm font-semibold text-[#D4722F] uppercase tracking-wide">{blogPageContent.hero.badgeText}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              {blogPageContent.hero.title}
            </h1>
            <p className="text-base sm:text-lg text-gray-400 max-w-2xl">
              {blogPageContent.hero.description}
            </p>
          </div>
        </section>

        {/* Tag filters */}
        {allTags.length > 0 && (
          <div className="sticky top-20 z-40 bg-white border-b border-gray-200 py-4 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm font-medium text-[#3B3535]/60 mr-1">Filter:</span>
                <button
                  data-testid="blog-filter-all"
                  onClick={() => setActiveTag(null)}
                  className={`text-sm font-medium px-4 py-1.5 rounded-full transition-all ${
                    !activeTag ? 'bg-[#B5541B] text-white' : 'bg-[#B5541B]/10 text-[#B5541B] hover:bg-[#B5541B]/20'
                  }`}
                >
                  All
                </button>
                {allTags.map((t) => (
                  <button
                    key={t}
                    data-testid={`blog-filter-${t.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setActiveTag(t)}
                    className={`text-sm font-medium px-4 py-1.5 rounded-full transition-all ${
                      activeTag === t ? 'bg-[#B5541B] text-white' : 'bg-[#B5541B]/10 text-[#B5541B] hover:bg-[#B5541B]/20'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Posts grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {loading && (
            <div className="flex items-center justify-center py-20" data-testid="blog-loading">
              <Loader2 className="w-8 h-8 animate-spin text-[#B5541B]" />
            </div>
          )}

          {error && (
            <div className="text-center py-20" data-testid="blog-error">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && visiblePosts.length === 0 && (
            <div className="text-center py-20 text-[#3B3535]/60" data-testid="blog-empty">
              <Newspaper className="w-12 h-12 mx-auto mb-4 opacity-40" />
              <p>{activeTag ? `No posts yet for "${activeTag}".` : blogPageContent.emptyState.message}</p>
            </div>
          )}

          {!loading && visiblePosts.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="blog-post-grid">
              {visiblePosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-post-card-${post.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#B5541B]/30 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col"
                >
                  {post.cover_url ? (
                    <div className="aspect-[16/9] overflow-hidden bg-[#FFF8F5]">
                      <img
                        src={post.cover_url}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-[#B5541B]/10 via-[#FFF8F5] to-[#D4722F]/10 flex items-center justify-center">
                      <Newspaper className="w-12 h-12 text-[#B5541B]/40" />
                    </div>
                  )}

                  <div className="p-6 flex flex-col flex-1">
                    {post.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags.slice(0, 3).map((t) => (
                          <span key={t} className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#B5541B]/10 text-[#B5541B]">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}

                    <h2 className="text-xl font-bold text-[#1F1E1E] mb-3 group-hover:text-[#B5541B] transition-colors leading-snug">
                      {post.title}
                    </h2>

                    {post.excerpt && (
                      <p className="text-sm text-[#3B3535]/70 leading-relaxed mb-5 line-clamp-3">
                        {post.excerpt}
                      </p>
                    )}

                    <div className="mt-auto flex items-center justify-between text-xs text-[#3B3535]/50 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-3">
                        {post.published_date && (
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(post.published_date)}
                          </span>
                        )}
                        {post.reading_time_minutes && (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {post.reading_time_minutes} min
                          </span>
                        )}
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#B5541B] group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default BlogIndexPage;
